import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { processesApiService, Process, ProcessCreateRequest } from '../services/processesApi';
import { workAreaApiService, WorkArea } from '../services/workAreaApi';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';

export function ProcessInformationForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [current, setCurrent] = useState<Process | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);

  const [form, setForm] = useState<ProcessCreateRequest>({
    processName: '',
    description: '',
    workAreaId: 0,
    isActive: true,
    createdBy: user?.id || 1,
    updatedBy: user?.id || 1,
  });

  useEffect(() => {
    if (id && window.location.pathname.includes('/view')) setViewMode(true);
    if (id && window.location.pathname.includes('/edit')) setViewMode(false);
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        const workAreaList = selectedBranch
          ? await workAreaApiService.getActiveByBranch(selectedBranch)
          : [];
        setWorkAreas(Array.isArray(workAreaList) ? workAreaList : []);

        if (id) {
          const process = await processesApiService.getProcessById(id);
          setCurrent(process);
          setForm({
            processName: process.processName || '',
            description: process.description || '',
            workAreaId: process.workAreaId || process.workArea?.id || 0,
            isActive: process.isActive ?? true,
            createdBy: process.createdBy || user?.id || 1,
            updatedBy: user?.id || process.updatedBy || 1,
          });
        } else {
          setForm((prev) => ({
            ...prev,
            createdBy: user?.id || 1,
            updatedBy: user?.id || 1,
          }));
        }
      } catch {
        // toasted in services
      }
    };
    void init();
  }, [id, selectedBranch, user?.id]);

  const handleChange = (key: keyof ProcessCreateRequest, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.processName.trim()) {
      toast.error('Please enter process name');
      return;
    }
    if (!form.workAreaId) {
      toast.error('Please select a work area');
      return;
    }

    setLoading(true);
    try {
      const payload: ProcessCreateRequest = {
        ...form,
        processName: form.processName.trim(),
        description: form.description.trim(),
        updatedBy: user?.id || form.updatedBy || 1,
        createdBy: current?.createdBy || user?.id || 1,
      };

      if (current?.id) {
        await processesApiService.updateProcess(String(current.id), { ...payload, id: current.id });
      } else {
        await processesApiService.createProcess(payload);
      }
      navigate('/processes');
    } catch {
      // toasted in service
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/processes');

  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleCancel} className="text-gray-700 hover:bg-gray-50">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl text-gray-900">
              {viewMode ? 'View Process' : current ? 'Edit Process' : 'Add New Process'}
            </CardTitle>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Basic Information
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>
                  Process Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.processName}
                  onChange={(e) => handleChange('processName', e.target.value)}
                  placeholder="Enter process name"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Work Area <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.workAreaId ? String(form.workAreaId) : undefined}
                  onValueChange={(value) => handleChange('workAreaId', parseInt(value, 10))}
                  disabled={viewMode}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="Select work area" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search work area..." debounceMs={300}>
                    {form.workAreaId > 0 &&
                      !workAreas.some((wa) => wa.id === form.workAreaId) && (
                        <SelectItem value={String(form.workAreaId)}>
                          Work area #{form.workAreaId}
                        </SelectItem>
                      )}
                    {workAreas.map((wa) => (
                      <SelectItem key={wa.id} value={String(wa.id)}>
                        {wa.workAreaName}
                        {wa.code ? ` (${wa.code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Enter description"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 min-h-[100px]"
                  disabled={viewMode}
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={form.isActive === true}
                      onChange={() => handleChange('isActive', true)}
                      disabled={viewMode}
                      className="text-primary-600 focus:ring-primary-400"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={form.isActive === false}
                      onChange={() => handleChange('isActive', false)}
                      disabled={viewMode}
                      className="text-primary-600 focus:ring-primary-400"
                    />
                    <span>Inactive</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {!viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                disabled={loading}
              >
                {loading ? 'Saving...' : current ? 'Update Process' : 'Create Process'}
              </Button>
            </div>
          )}

          {viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
